import {parseStringToTags} from '@coolcolduk/util';
import {JournalSession, TradeStatus, type Trade} from '@cp/model';
import {Autocomplete, TextField} from '@mui/material';
import SwitchTradeDirection from '../../atoms/switchTradeDirection/SwitchTradeDirection';
import SelectTradeStatus from '../../atoms/selectTradeStatus/SelectTradeStatus';

export interface TradeFormProps {
  data: Trade;
  setData: (next: Trade) => void;
  journalSessions?: JournalSession[];
}

function displayNumberInput(n: number): string {
  if (!Number.isFinite(n) || Number.isNaN(n) || n === 0) return '';
  return String(n);
}

function parseNumberInput(value: string): number {
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  return Number(trimmed);
}

export default function TradeForm({data, setData, journalSessions}: TradeFormProps) {
  const hasJournalSessions = journalSessions !== undefined && journalSessions.length > 0;

  const sessionValue = data.sessionId ?? '';
  const tradeIdValue = data.tradeId ?? '';
  const entryPriceValue = displayNumberInput(data.entryPrice);
  const exitPriceValue = data.status === TradeStatus.OPEN ? '' : displayNumberInput(data.exitPrice);
  const profitLossValue = data.status === TradeStatus.OPEN ? '' : displayNumberInput(data.profitLoss);
  const plOverrideInvalid = data.status !== TradeStatus.OPEN && Number.isNaN(data.profitLoss);
  const tagsValue = (data.tags ?? []).join(', ');

  return (
    <>
      <TextField
        label="Symbol"
        size="small"
        value={data.symbol ?? ''}
        onChange={(e) => setData({...data, symbol: e.target.value})}
        fullWidth
      />

      {hasJournalSessions ? (
        <Autocomplete
          freeSolo
          options={journalSessions}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.id)}
          inputValue={sessionValue}
          onInputChange={(_, value) => setData({...data, sessionId: value.trim()})}
          onChange={(_, value) => {
            if (typeof value === 'string') {
              setData({...data, sessionId: value.trim()});
              return;
            }
            if (value && typeof value === 'object') {
              setData({...data, sessionId: (value as JournalSession).id});
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Session ID" size="small" helperText="Choose a session or type a new id" />
          )}
          sx={{width: '100%'}}
        />
      ) : (
        <TextField
          label="Session ID"
          size="small"
          value={sessionValue}
          onChange={(e) => {
            const trimmed = e.target.value.trim();
            setData({...data, sessionId: trimmed === '' ? '' : trimmed});
          }}
          placeholder="YYYYMMDD-HHmmss"
          fullWidth
        />
      )}

      <TextField
        label="Trade id (replay/broker, optional)"
        size="small"
        value={tradeIdValue}
        onChange={(e) => {
          const trimmed = e.target.value.trim();
          setData({...data, tradeId: trimmed === '' ? '' : trimmed});
        }}
        placeholder="e.g. replay row number"
        sx={{gridColumn: {sm: '1 / -1'}}}
        fullWidth
      />

      <SwitchTradeDirection data={data} setData={setData} />

      <SelectTradeStatus data={data} setData={setData} />

      <TextField
        label="Entry (open) price"
        size="small"
        type="number"
        value={entryPriceValue}
        onChange={(e) => setData({...data, entryPrice: parseNumberInput(e.target.value)})}
        fullWidth
      />

      <TextField
        label="Exit (close) price"
        size="small"
        type="number"
        value={exitPriceValue}
        onChange={(e) => setData({...data, exitPrice: parseNumberInput(e.target.value)})}
        disabled={data.status === TradeStatus.OPEN}
        fullWidth
      />

      <TextField
        label="P/L override (optional)"
        size="small"
        type="number"
        value={profitLossValue}
        onChange={(e) => setData({...data, profitLoss: parseNumberInput(e.target.value)})}
        disabled={data.status === TradeStatus.OPEN}
        error={plOverrideInvalid}
        sx={{gridColumn: {sm: '1 / -1'}}}
        fullWidth
        helperText={
          plOverrideInvalid
            ? 'P/L override must be a valid number'
            : 'Leave empty to omit stored P/L; lists still show P/L from entry, exit, and direction'
        }
      />

      <TextField
        label="Tags (comma separated)"
        size="small"
        value={tagsValue}
        onChange={(e) => setData({...data, tags: parseStringToTags(e.target.value)})}
        sx={{gridColumn: {sm: '1 / -1'}}}
        fullWidth
      />
    </>
  );
}
